import { UI, Stack, VStack } from '@genial/components';
import { Color, Spacing } from '@genial/design-tokens';
import React, { useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { getHomeValueVisibility } from '../../../../../../store/home/capture/selector';
import { customersActivationByProfile } from '../../../../../../store/home/conversion/selector';
import { PerformanceSummaryMetric } from '../../../PerformanceSummaryMetric';

export interface CustomersActivationByProfileContentProps {
  isMobile?: boolean;
}

export const CustomersActivationByProfileContent: React.FC<
  CustomersActivationByProfileContentProps
> = ({ isMobile }) => {
  const valueVisible = useRecoilValue(getHomeValueVisibility);

  const {
    data: customersActivationByProfileData,
    loading: customersActivationByProfileLoading,
    refresher: refresherCustomersActivationByProfile,
    error: errorCustomersActivationByProfile,
  } = useRecoilQuery(customersActivationByProfile);

  const charContent = useMemo(() => {
    const bars = [
      {
        color: 'risk.conservador',
        label: 'Perfil conversador',
        dataKey: 'conservativeValue',
      } as const,
      {
        color: 'risk.moderado',
        label: 'Perfil Moderado',
        dataKey: 'moderateValue',
      } as const,
      {
        color: 'risk.arrojado',
        label: 'Perfil Arrojado',
        dataKey: 'aggressiveValue',
      } as const,
      {
        color: 'brand.secondary.dark',
        label: 'Perfil Qualificado/Profissional',
        dataKey: 'qualifiedOrProfessionalValue',
      } as const,
      {
        color: 'brand.tertiary.principal',
        label: 'Sem Perfil - Recusa',
        dataKey: 'withoutProfileValue',
      } as const,
    ];

    const additionalData = {
      year: customersActivationByProfileData?.data?.performanceYear ?? 2024,
      tooltipLabels: [...bars],
    };

    if (errorCustomersActivationByProfile) {
      return (
        <Stack height={237} w="full" px={Spacing['inline.xxs']}>
          <UI.ErrorBoundary
            onRefresh={refresherCustomersActivationByProfile}
            refreshText="RECARREGAR"
            refreshButtonCustomProps={{
              variant: 'textLink',
              rightIcon: (
                <UI.Icon name="Refresh" size="xxs" color={Color['brand.tertiary.principal']} />
              ),
            }}
          />
        </Stack>
      );
    }

    if (customersActivationByProfileLoading) {
      return (
        <Stack height={237} w="full" px={Spacing['inline.xxs']}>
          <UI.ChartBarsLoading isMobile={isMobile} barsLength={12} />
        </Stack>
      );
    }

    return (
      <UI.QualificationsByProfileChart
        data={customersActivationByProfileData?.data.performanceData ?? []}
        additionalData={additionalData}
        bars={bars}
        isMobile={isMobile}
      />
    );
  }, [
    customersActivationByProfileData,
    customersActivationByProfileLoading,
    errorCustomersActivationByProfile,
    isMobile,
    refresherCustomersActivationByProfile,
  ]);

  const bigNumbers = customersActivationByProfileData?.data.bigNumbers;

  return (
    <VStack space={Spacing['stack.xxs']} direction={isMobile ? 'column-reverse' : 'column'}>
      <Stack
        space={Spacing['stack.xxs']}
        justifyContent="space-between"
        px={Spacing['inline.xxs']}
        direction={isMobile ? 'column' : 'row'}>
        <PerformanceSummaryMetric
          label="YTD"
          value={bigNumbers?.yearToDateValue ?? 0}
          performanceComparisonLabel="YoY"
          performanceComparisonValue={bigNumbers?.previousYearValue ?? 0}
          performanceComparisonPercentage={bigNumbers?.yoYPercentage ?? 0}
          hideValue={!valueVisible}
          isMobile={isMobile}
          isLoading={customersActivationByProfileLoading}
          withError={!!errorCustomersActivationByProfile}
        />
        <PerformanceSummaryMetric
          label="QTD"
          value={bigNumbers?.quarterToDateValue ?? 0}
          performanceComparisonLabel="QoQ"
          performanceComparisonValue={bigNumbers?.previousQuarterValue ?? 0}
          performanceComparisonPercentage={bigNumbers?.qoQPercentage ?? 0}
          hideValue={!valueVisible}
          isMobile={isMobile}
          isLoading={customersActivationByProfileLoading}
          withError={!!errorCustomersActivationByProfile}
        />
        <PerformanceSummaryMetric
          label="MTD"
          value={bigNumbers?.monthToDateValue ?? 0}
          performanceComparisonLabel="MoM"
          performanceComparisonValue={bigNumbers?.previousMonthValue ?? 0}
          performanceComparisonPercentage={bigNumbers?.moMPercentage ?? 0}
          hideValue={!valueVisible}
          isMobile={isMobile}
          isLoading={customersActivationByProfileLoading}
          withError={!!errorCustomersActivationByProfile}
        />
        <PerformanceSummaryMetric
          label="WTD"
          value={bigNumbers?.weekToDateValue ?? 0}
          performanceComparisonLabel="WoW"
          performanceComparisonValue={bigNumbers?.previousWeekValue ?? 0}
          performanceComparisonPercentage={bigNumbers?.woWPercentage ?? 0}
          hideValue={!valueVisible}
          isMobile={isMobile}
          isLoading={customersActivationByProfileLoading}
          withError={!!errorCustomersActivationByProfile}
        />
      </Stack>
      {charContent}
    </VStack>
  );
};
